import React, { useEffect, useState } from 'react';

/**
 * Section Installation de Réglages - lot 5.
 *
 * Le bouton natif des navigateurs est capricieux : Chrome le masque dès que
 * l'application est installée ET après un refus, pendant plusieurs jours ;
 * Safari n'en a jamais eu ; Firefox pour ordinateur a retiré la fonction en
 * 2021. Résultat, « comment installer ? » n'a pas de réponse stable, et
 * l'utilisateur ne peut pas savoir si le bouton manque parce que c'est déjà
 * fait ou parce que son navigateur ne sait pas faire.
 *
 * Cette section répond toujours, quel que soit le navigateur : soit elle
 * installe, soit elle dit pourquoi elle ne peut pas et comment faire à la
 * main.
 */
export default function Installation() {
  const [invite, setInvite] = useState(null);
  const [installee, setInstallee] = useState(false);

  useEffect(() => {
    // Chrome émet cet événement quand tous les critères sont réunis et que
    // l'application n'est pas déjà installée. Il faut le retenir : il n'est
    // émis qu'une fois, et `prompt()` ne peut être appelé qu'en réponse à un
    // geste de l'utilisateur, donc plus tard.
    const capturer = (e) => { e.preventDefault(); setInvite(e); };
    const installe = () => { setInstallee(true); setInvite(null); };

    window.addEventListener('beforeinstallprompt', capturer);
    window.addEventListener('appinstalled', installe);

    // Lancée depuis l'écran d'accueil, l'application tourne en mode autonome :
    // c'est la seule détection fiable d'une installation faite lors d'une
    // session précédente.
    if (window.matchMedia?.('(display-mode: standalone)').matches ||
        window.navigator.standalone) {
      setInstallee(true);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', capturer);
      window.removeEventListener('appinstalled', installe);
    };
  }, []);

  const installer = async () => {
    if (!invite) return;
    invite.prompt();
    const { outcome } = await invite.userChoice;
    // L'événement n'est utilisable qu'une fois : le garder après un refus
    // laisserait un bouton qui ne fait plus rien.
    setInvite(null);
    if (outcome === 'accepted') setInstallee(true);
  };

  const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const firefox = /Firefox/.test(navigator.userAgent);

  return (
    <section>
      <h2 style={{ fontFamily: 'var(--police-titre)', fontSize: '1.1rem', margin: '0 0 var(--espace-3)' }}>
        Installation
      </h2>

      {installee ? (
        <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--couleur-encre-douce)' }}>
          Installée sur cet appareil. Elle s’ouvre depuis l’écran d’accueil et
          fonctionne sans réseau.
        </p>
      ) : invite ? (
        <div>
          <p style={{ margin: '0 0 var(--espace-3)', fontSize: '0.85rem', color: 'var(--couleur-encre-douce)' }}>
            Installer place l’application sur l’écran d’accueil, sans barre
            d’adresse, et la rend utilisable sans réseau.
          </p>
          <button
            type="button"
            onClick={installer}
            style={{
              padding: 'var(--espace-2) var(--espace-4)',
              border: '1px solid var(--couleur-accent)',
              borderRadius: 'var(--rayon)',
              background: 'var(--couleur-accent)',
              color: 'var(--couleur-sur-accent)',
              fontWeight: 600,
            }}
          >
            Installer
          </button>
        </div>
      ) : (
        <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--couleur-encre-douce)' }}>
          {iOS
            ? 'Sur iPhone et iPad, l’installation est manuelle : bouton Partager, puis « Sur l’écran d’accueil ». Safari ne propose jamais d’invite automatique.'
            : firefox
              ? 'Firefox pour ordinateur ne sait pas installer d’application web - la fonction a été retirée en 2021. Utilisez Chrome ou Edge. Le fonctionnement hors-ligne, lui, marche ici.'
              : 'Aucune invite d’installation disponible pour l’instant. Soit l’application est déjà installée, soit le navigateur attend avant de reproposer après un refus. Le menu du navigateur propose « Installer » en permanence.'}
        </p>
      )}
    </section>
  );
}
